import { useCityStore } from '../city/cityStore';
import { cityTime } from '../city/cityTime';
import PlayerBar from './PlayerBar';
import { createPortal } from 'react-dom';
import React, { useEffect, useRef, useState } from 'react';
import { weather } from '../render/weather';
import { radarShown, RADAR_GUTTER } from '../game/hud/GameHud';
import { useWorldStore, ZOOM } from '../state/worldStore';
import { audioEngine } from '../services/audioEngine';
import { api } from '../services/api';
import { SLICE_DISTRICT, IS_GUEST, HUD_MAX, HUD_FULLSCREEN, HUD_UP_FROM_STREET, HOME_URL } from '../config';

// Log out: clear the saved token AND strip ?token= from the URL (config re-saves
// it on load otherwise), then reload — AUTH_TOKEN comes back empty → guest.
function logout() {
  try { localStorage.removeItem('world:authToken'); } catch { /* private mode */ }
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete('token');
    window.location.replace(url.toString());
  } catch { window.location.reload(); }
}
import { TOWN_LOCATIONS, ROOMS } from '../rooms';
import { usePovStore } from '../game/player';
import { useHasBody } from '../hooks/usePov';
import { useVenueAnchors } from '../hooks/useVenueAnchors';
import { camBasis } from '../services/worldObjects';
import { useWorldTime } from '../worldTime';
import { serverNow } from '../services/worldSocket';

const SHORT = { studio: 'Studio', 'listening-room': 'Listening', 'record-store': 'Records', 'concert-hall': 'Concert', theater: 'Cinema', grocery: 'Market', salon: 'Salon', subway: 'Underground', warehouse: 'Warehouse' };
// Anything without a short label (a new venue) falls back to its room name.
const ROOM_NAME = (id) => ROOMS[id]?.name || id;
const MODEL_MODE = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('city') === 'model';

// Eye icon for the people toggle: open = people shown (active), slashed = hidden.
// Inherits the button's currentColor so it dims with the off state.
function EyeIcon({ off = false }) {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
      {off && <line x1="3.5" y1="3.5" x2="20.5" y2="20.5" />}
    </svg>
  );
}

// The minimap AUTO-FITS to whatever it's showing (the venues + any present
// follows), so nothing clamps to a corner. Build a world(x,z) → viewBox(100×80)
// projection from a set of world points: fit their bounding box into an inset
// frame, preserving relative positions. The venue block alone reproduces the
// classic spread; a far-flung follower just zooms the frame out to include it.
function makeProject(worldPts) {
  const PAD_X = 12, PAD_Y = 12, W = 100, H = 80;         // viewBox inset
  const xs = worldPts.map((p) => p[0]); const zs = worldPts.map((p) => p[1]);
  let minX = Math.min(...xs), maxX = Math.max(...xs);
  let minZ = Math.min(...zs), maxZ = Math.max(...zs);
  // Guard degenerate spans (single point / collinear) so we never divide by 0.
  if (maxX - minX < 1) { minX -= 20; maxX += 20; }
  if (maxZ - minZ < 1) { minZ -= 20; maxZ += 20; }
  // Z is inverted (1 − …) so NORTH is UP: larger world z (deeper into the block)
  // sits toward the top, matching the venues' original map orientation.
  return (x, z) => [
    PAD_X + ((x - minX) / (maxX - minX)) * (W - 2 * PAD_X),
    PAD_Y + (1 - (z - minZ) / (maxZ - minZ)) * (H - 2 * PAD_Y),
  ];
}

// Track viewport width so the chrome can reflow for phones (inline styles don't
// respond to media queries on their own).
function useViewport() {
  const [w, setW] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  useEffect(() => {
    const on = () => setW(window.innerWidth);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return w;
}

// Uniform-scale projection for the model city: the map is only honest about
// DIRECTION if x and z share one scale (the stretch-to-fit above squashes one
// axis whenever the frame isn't 5:4, which turns "the studio is north-east of
// you" into a lie). North (+z) is up, as before.
function makeUniformProject(worldPts) {
  const PAD = 10, W = 100, H = 80;
  const xs = worldPts.map((p) => p[0]); const zs = worldPts.map((p) => p[1]);
  let minX = Math.min(...xs), maxX = Math.max(...xs);
  let minZ = Math.min(...zs), maxZ = Math.max(...zs);
  if (maxX - minX < 1) { minX -= 20; maxX += 20; }
  if (maxZ - minZ < 1) { minZ -= 20; maxZ += 20; }
  const k = Math.min((W - 2 * PAD) / (maxX - minX), (H - 2 * PAD) / (maxZ - minZ));
  const ox = (W - (maxX - minX) * k) / 2, oy = (H - (maxZ - minZ) * k) / 2;
  return (x, z) => [ox + (x - minX) * k, oy + (maxZ - z) * k];
}

// Labels that neither overlap each other nor run off the map. Greedy: each
// label tries above, below, right, then left of its dot and takes the first
// spot that is clear and inside the frame. Eight venues is far too few for
// anything cleverer to matter — the old labels simply all sat above their dots,
// so "Studio", "The Salon" and "The Warehouse" printed over one another and
// the edge ones were clipped ("rner Market").
const LABEL_FS = 4.6;
function layoutLabels(items, first = []) {
  const W = 100, H = 80, boxes = [];
  // Who gets a label first when there is not room for all of them: the story's
  // destination and where you are, then everyone else in list order.
  const rank = (it) => { const i = first.indexOf(it.loc?.key); return i < 0 ? first.length : i; };
  const order = items.map((it, i) => [it, i]).sort((a, b) => rank(a[0]) - rank(b[0]) || a[1] - b[1]);
  const hit = (a) => a.x0 < 1 || a.y0 < 1 || a.x1 > W - 1 || a.y1 > H - 1
    || boxes.some((b) => a.x0 < b.x1 && a.x1 > b.x0 && a.y0 < b.y1 && a.y1 > b.y0);
  // Dots are obstacles too.
  for (const it of items) boxes.push({ x0: it.cx - 3.8, y0: it.cy - 3.8, x1: it.cx + 3.8, y1: it.cy + 3.8 });
  const out = new Array(items.length);
  for (const [it, idx] of order) {
    const w = it.text.length * LABEL_FS * 0.56 + 1.5, h = LABEL_FS + 0.8;
    const opts = [
      { x: it.cx, y: it.cy - 5.2, anchor: 'middle', x0: it.cx - w / 2, y0: it.cy - 5.2 - h + 1, x1: it.cx + w / 2, y1: it.cy - 4.2 },
      { x: it.cx, y: it.cy + 9.4, anchor: 'middle', x0: it.cx - w / 2, y0: it.cy + 4.4, x1: it.cx + w / 2, y1: it.cy + 9.4 + 1 },
      { x: it.cx + 5.2, y: it.cy + 1.8, anchor: 'start', x0: it.cx + 4.4, y0: it.cy - h / 2, x1: it.cx + 5.2 + w, y1: it.cy + h / 2 },
      { x: it.cx - 5.2, y: it.cy + 1.8, anchor: 'end', x0: it.cx - 5.2 - w, y0: it.cy - h / 2, x1: it.cx - 4.4, y1: it.cy + h / 2 },
      { x: it.cx + 3.5, y: it.cy - 4.2, anchor: 'start', x0: it.cx + 3, y0: it.cy - 4.2 - h + 1, x1: it.cx + 3.5 + w, y1: it.cy - 3.2 },
      { x: it.cx - 3.5, y: it.cy - 4.2, anchor: 'end', x0: it.cx - 3.5 - w, y0: it.cy - 4.2 - h + 1, x1: it.cx - 3, y1: it.cy - 3.2 },
      { x: it.cx + 3.5, y: it.cy + 8.2, anchor: 'start', x0: it.cx + 3, y0: it.cy + 3.2, x1: it.cx + 3.5 + w, y1: it.cy + 8.2 + 1 },
      { x: it.cx - 3.5, y: it.cy + 8.2, anchor: 'end', x0: it.cx - 3.5 - w, y0: it.cy + 3.2, x1: it.cx - 3, y1: it.cy + 8.2 + 1 },
    ];
    // No clear spot: the label is not drawn (it still shows on hover and as
    // the dot's tooltip). A pile of overprinted names reads as noise, which
    // is worse than a dot you have to point at.
    const pick = opts.find((o) => !hit(o));
    if (pick) boxes.push(pick);
    const p = pick || opts[0];
    out[idx] = { ...it, crowded: !pick, lx: Math.max(1 + (p.anchor === 'start' ? 0 : p.anchor === 'end' ? w : w / 2), Math.min(99 - (p.anchor === 'end' ? 0 : p.anchor === 'start' ? w : w / 2), p.x)), ly: Math.max(LABEL_FS, Math.min(79, p.y)), anchor: p.anchor };
  }
  return out;
}

// TownMap — a clickable HUD minimap of the block. A dot per location, placed
// at its layout coord. Click travels the camera to that spot; clicking the
// venue you're already at steps inside. Highlights where you are now.
function TownMap({ narrow = false }) {
  const streetFocus = useWorldStore((s) => s.streetFocus);
  const setStreetFocus = useWorldStore((s) => s.setStreetFocus);
  const setPendingEnter = useWorldStore((s) => s.setPendingEnter);
  const setWalkTarget = useWorldStore((s) => s.setWalkTarget);
  const venueAnchors = useWorldStore((s) => s.venueAnchors);
  const liveAnchors = useVenueAnchors();
  const waypoint = useWorldStore((s) => s.waypoint);
  const interior = useWorldStore((s) => s.interior);
  const npcList = useWorldStore((s) => s.npcList);
  const following = useWorldStore((s) => s.following);
  const owned = useWorldStore((s) => s.owned);
  const showPeople = useWorldStore((s) => s.showPeople);
  const toggleShowPeople = useWorldStore((s) => s.toggleShowPeople);
  const online = useWorldStore((s) => s.online); // live participant count (/world)
  // THE BIG MAP (Khari, 2026-10-23: "we still need the map enlarged. Perhaps
  // center it and enlarge. Be sure the close button is more visible."). On a
  // phone the chip opens it; on a desktop the corner minimap stays and opens it.
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    if (!expanded) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setExpanded(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expanded]);
  const active = interior || streetFocus;

  // Residents you OWN or FOLLOW that are present here → headshot pins on the map
  // (mine ∪ following ∩ roster; never thousands). `mine` wins for the ring color.
  // Tapping one fast-travels to them. Hidden when the people toggle is off (a lot
  // of owned avatars otherwise cloud the map).
  //
  // ⚠ OFF SINCE THE LIVING CITY (2026-10-23). Khari: *"we don't tell people where
  // things are happening, and let them explore freely, stumbling across dynamic,
  // or timed events."* The artists walk their own days now (city/), so a face on
  // the map is a spoiler — and the pin read a post nobody stands on any more.
  // Finding them is walking the city.
  const allMarks = [];
  const followMarks = showPeople ? allMarks : [];

  // WHERE THE VENUES REALLY ARE. In the model city a venue stands on whichever
  // building CityModel anchored it to (sampler, rooms.js pin, or a hand
  // placement) — TOWN_LOCATIONS.world is the old hand-built street's layout and
  // can be a long way from the actual door. The map used to draw the layout,
  // so it disagreed with the markers above the buildings.
  const modelMap = MODEL_MODE && !!liveAnchors;
  const venueXZ = (loc) => {
    const a = modelMap ? liveAnchors[loc.key] : null;
    return a ? [a[0], a[2]] : (Array.isArray(loc.world) ? loc.world : null);
  };
  const anchorKey = modelMap ? TOWN_LOCATIONS.map((l) => (liveAnchors[l.key] || []).map((v) => Math.round(v)).join(':')).join('|') : 'layout';
  // Auto-fit projection over the venues + any present follows. Framed on the
  // PLACES, not the whole city footprint: the city is a long thin strip, and at
  // one uniform scale fitting all of it crushed the venues into a knot in the
  // middle. You can walk off this frame — the arrow then waits on its edge,
  // pointing the way back.
  const project = React.useMemo(() => {
    const pts = [
      ...(modelMap ? TOWN_LOCATIONS.filter((l) => liveAnchors[l.key]) : TOWN_LOCATIONS).map(venueXZ).filter(Boolean),
      ...followMarks.map((n) => [n.post[0], n.post[2]]),
    ];
    return modelMap ? makeUniformProject(pts) : makeProject(pts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [followMarks.map((n) => n.avatarId).join(','), anchorKey, modelMap]);

  // In the model city a venue with no anchor has no place on THIS map — its
  // layout coordinate belongs to a different street. It is listed under the
  // map instead, still one tap away.
  const placed = modelMap ? TOWN_LOCATIONS.filter((l) => liveAnchors[l.key]) : TOWN_LOCATIONS;
  const unplaced = modelMap ? TOWN_LOCATIONS.filter((l) => !liveAnchors[l.key] && l.interiorId) : [];
  const venues = React.useMemo(() => layoutLabels(placed.map((loc) => {
    const xz = venueXZ(loc);
    const [cx, cy] = xz ? project(xz[0], xz[1]) : [loc.map[0] * 100, loc.map[1] * 80];
    return { loc, cx, cy, text: SHORT[loc.key] || loc.name };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [waypoint, active].filter(Boolean)), [project, waypoint, active]);
  const [hoverKey, setHoverKey] = useState(null);

  // YOU ARE HERE. Written straight to the SVG node ten times a second from the
  // walk rig's published position — never through React state, which would
  // re-render the whole HUD on every step.
  const meRef = useRef(null);
  const showMe = modelMap && !interior;
  useEffect(() => {
    if (!showMe) return undefined;
    const id = setInterval(() => {
      const g = meRef.current;
      if (!g) return;
      let [x, y] = project(camBasis.px, camBasis.pz);
      const off = x < 4 || x > 96 || y < 4 || y > 76;
      x = Math.max(4, Math.min(96, x)); y = Math.max(4, Math.min(76, y));
      const deg = (Math.atan2(camBasis.fx, camBasis.fz) * 180) / Math.PI;
      g.setAttribute('transform', `translate(${x.toFixed(2)} ${y.toFixed(2)}) rotate(${deg.toFixed(1)})`);
      g.style.opacity = off ? '0.55' : '1';
    }, 100);
    return () => clearInterval(id);
  }, [showMe, project]);
  const goToResident = (n) => {
    const [x, , z] = n.post;
    // Smooth flyover to a spot beside them, facing them (walkTarget = the same
    // quick-travel the venue dots use; y is overridden to eye level downstream).
    setWalkTarget({ pos: [x + 3.5, 0, z + 3.5], look: [x, 0, z] });
  };

  const onPick = (loc) => {
    setExpanded(false);
    // In the walkable model city: quick-travel (teleport) to the venue's
    // building; the beacon there steps you inside.
    if (MODEL_MODE && venueAnchors && venueAnchors[loc.key]) {
      const a = venueAnchors[loc.key];
      setStreetFocus(loc.key);
      setWalkTarget({ pos: [a[0] + 6, 1.7, a[2] + 6], look: [a[0], 1.5, a[2]], enter: loc.interiorId });
      return;
    }
    // Elsewhere: fly across town to the venue, then auto-enter on arrival.
    if (loc.interiorId) { setPendingEnter(loc.key); setStreetFocus(loc.key); }
    else setStreetFocus(loc.key);
  };

  // Collapsed on phones: a small chip that opens the big map.
  if (narrow && !expanded) {
    return (
      <button onClick={() => setExpanded(true)} style={{
        position: 'absolute', right: 'max(12px, env(safe-area-inset-right))', top: 'max(12px, env(safe-area-inset-top))',
        pointerEvents: 'auto', background: 'rgba(16,10,6,0.72)', backdropFilter: 'blur(6px)',
        border: '1px solid rgba(255,178,94,0.35)', borderRadius: 999, color: '#ffe9c4',
        padding: '8px 13px', fontSize: 12.5, fontFamily: 'system-ui, sans-serif',
      }}>🗺 Map{waypoint ? ' ·' : ''}{waypoint && <span style={{ color: '#9dff7d' }}> ●</span>}</button>
    );
  }
  const big = expanded;
  // The big map's drawing is as wide as the panel allows; the viewBox is 100×80.
  const bigW = typeof window !== 'undefined' ? Math.min(window.innerWidth - 64, 560) : 480;
  const svgW = big ? bigW : 166, svgH = big ? Math.round(bigW * 0.8) : 133;
  const panel = (
    <div
      onClick={(e) => e.stopPropagation()}
      style={big ? {
        position: 'relative', width: bigW + 32, maxWidth: 'calc(100vw - 32px)', boxSizing: 'border-box', pointerEvents: 'auto',
        background: 'rgba(16,10,6,0.92)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
        border: '1px solid rgba(255,178,94,0.45)', borderRadius: 18, padding: '14px 16px 14px',
        color: '#ffe9c4', fontFamily: 'system-ui, sans-serif', boxShadow: '0 24px 70px rgba(0,0,0,0.55)',
      } : {
        position: 'absolute', right: 16, top: 16,
        width: 190, pointerEvents: 'auto',
        background: 'rgba(16,10,6,0.72)', backdropFilter: 'blur(6px)',
        border: '1px solid rgba(255,178,94,0.35)', borderRadius: 12, padding: '10px 12px 8px',
        color: '#ffe9c4', fontFamily: 'system-ui, sans-serif',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: big ? 10 : 6 }}>
        <span style={{ fontSize: big ? 12.5 : 10.5, letterSpacing: 1.4, textTransform: 'uppercase', opacity: big ? 0.9 : 0.75, fontWeight: big ? 700 : 400 }}>HypeTown · map</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {/* Live participant count — everyone connected to the world right now. */}
          {online > 0 && (
            <span title={`${online} online now (including you)`} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#9dff7d', fontVariantNumeric: 'tabular-nums' }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#9dff7d', boxShadow: '0 0 6px #9dff7d' }} />
              {online}
            </span>
          )}
          {/* People toggle — hid the owned/followed avatar pins. There are no
              pins since the Living City (see allMarks), so no toggle either. */}
          {allMarks.length > 0 && <button
            onClick={toggleShowPeople}
            title={showPeople ? 'Hide people on map' : 'Show people on map'}
            aria-pressed={showPeople}
            style={{
              display: 'flex', alignItems: 'center', gap: 3, cursor: 'pointer', padding: '2px 6px',
              borderRadius: 999, fontSize: 11, lineHeight: 1, fontFamily: 'system-ui, sans-serif',
              border: `1px solid ${showPeople ? 'rgba(255,210,125,0.6)' : 'rgba(255,255,255,0.18)'}`,
              background: showPeople ? 'rgba(255,210,125,0.16)' : 'transparent',
              color: showPeople ? '#ffd27d' : 'rgba(255,233,196,0.5)',
            }}
          >
            <EyeIcon off={!showPeople} />
            {allMarks.length > 0 && <span style={{ fontVariantNumeric: 'tabular-nums' }}>{allMarks.length}</span>}
          </button>}
          {big ? (
            // A close button you can SEE (and hit with a thumb).
            <button
              type="button" onClick={() => setExpanded(false)} aria-label="Close map" title="Close map"
              style={{
                width: 36, height: 36, borderRadius: '50%', cursor: 'pointer', marginLeft: 4,
                border: '1px solid rgba(255,178,94,0.6)', background: 'rgba(255,178,94,0.2)', color: '#fff',
                font: '700 17px system-ui', lineHeight: 1, display: 'grid', placeItems: 'center', padding: 0,
              }}
            >✕</button>
          ) : (
            <button
              type="button" onClick={() => setExpanded(true)} aria-label="Open the big map" title="Open the big map"
              style={{ cursor: 'pointer', background: 'none', border: 'none', color: '#ffd27d', fontSize: 13, padding: '0 2px', lineHeight: 1 }}
            >⤢</button>
          )}
        </div>
      </div>
      <svg viewBox="0 0 100 80" width={svgW} height={svgH} style={{ display: 'block', overflow: 'hidden' }}>
        {/* faint block footprint (the map auto-fits, so no fixed street/alley
            decor — it wouldn't line up once the frame zooms to the follows) */}
        <rect x="2" y="2" width="96" height="76" rx="4" fill="rgba(255,255,255,0.03)" stroke="rgba(255,178,94,0.18)" strokeWidth="0.6" />
        {venues.map(({ loc, cx, cy, lx, ly, anchor, text, crowded }) => {
          const on = active === loc.key;
          const goal = waypoint === loc.key;
          const showLabel = !crowded || hoverKey === loc.key;
          return (
            <g key={loc.key} onClick={() => onPick(loc)} style={{ cursor: 'pointer' }}
              onPointerEnter={() => setHoverKey(loc.key)} onPointerLeave={() => setHoverKey((k) => (k === loc.key ? null : k))}>
              <title>{loc.name}</title>
              {goal && (
                <circle cx={cx} cy={cy} r="4" fill="none" stroke="#9dff7d" strokeWidth="1.1">
                  <animate attributeName="r" values="4;9;4" dur="1.8s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.95;0;0.95" dur="1.8s" repeatCount="indefinite" />
                </circle>
              )}
              {on && <circle cx={cx} cy={cy} r="6.5" fill="none" stroke={loc.accent} strokeWidth="1.2" opacity="0.9" />}
              <circle cx={cx} cy={cy} r="3.6" fill={loc.accent} stroke={goal ? '#9dff7d' : '#1a0f00'} strokeWidth={goal ? 1 : 0.6} />
              {showLabel && (
                <text x={lx} y={ly} fill={on || goal ? '#fff' : '#ffe9c4'} fontSize={LABEL_FS} textAnchor={anchor}
                  style={{ fontWeight: on || goal ? 700 : 500, paintOrder: 'stroke', stroke: 'rgba(16,10,6,0.85)', strokeWidth: 1.4 }}>
                  {text}
                </text>
              )}
            </g>
          );
        })}
        {/* Followed residents present in the district — headshot pins with a
            gold ring; tap to fast-travel to them. */}
        {followMarks.map((n) => {
          const [cx, cy] = project(n.post[0], n.post[2]);
          const clip = `fp${n.avatarId}`;
          const first = (n.name || '').split(' ')[0];
          const ring = n.mine ? '#4fd1ff' : '#ffd27d'; // mine = teal · following = gold
          return (
            <g key={clip} onClick={() => goToResident(n)} style={{ cursor: 'pointer' }}>
              <defs><clipPath id={clip}><circle cx={cx} cy={cy} r="4.4" /></clipPath></defs>
              {n.headshotUrl
                ? <image href={n.headshotUrl} x={cx - 4.4} y={cy - 4.4} width="8.8" height="8.8" clipPath={`url(#${clip})`} preserveAspectRatio="xMidYMid slice" />
                : <circle cx={cx} cy={cy} r="4.4" fill={ring} />}
              <circle cx={cx} cy={cy} r="4.4" fill="none" stroke={ring} strokeWidth="1.1" />
              <circle cx={cx} cy={cy} r="5.8" fill="none" stroke={ring} strokeWidth="0.5" opacity="0.5" />
              <text x={cx} y={cy + 9} fill="#ffe9c4" fontSize="4.6" textAnchor="middle" style={{ fontWeight: 700 }}>{first}</text>
            </g>
          );
        })}
        {/* You. An arrow, because on a map the useful thing to know besides
            where you are is which way you are facing. */}
        {showMe && (
          <g ref={meRef} style={{ pointerEvents: 'none', transition: 'opacity 0.2s' }}>
            <circle r="5" fill="rgba(255,255,255,0.12)" />
            <path d="M0 -4.2 L3 3 L0 1.6 L-3 3 Z" fill="#ffffff" stroke="#1a0f00" strokeWidth="0.7" strokeLinejoin="round" />
          </g>
        )}
      </svg>
      {unplaced.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 5 }}>
          {unplaced.map((loc) => (
            <button key={loc.key} onClick={() => onPick(loc)} style={{
              pointerEvents: 'auto', cursor: 'pointer', padding: '2px 7px', borderRadius: 999,
              fontSize: 10, fontFamily: 'system-ui, sans-serif', color: '#ffe9c4',
              border: `1px solid ${loc.accent}66`, background: `${loc.accent}14`,
            }}>
              <span style={{ color: loc.accent }}>●</span> {SHORT[loc.key] || loc.name}
            </button>
          ))}
        </div>
      )}
      <div style={{ fontSize: 10.5, opacity: 0.7, marginTop: 4, lineHeight: 1.35 }}>
        {interior
          ? <>Inside <b>{SHORT[interior] || ROOM_NAME(interior)}</b> · tap another to switch</>
          : waypoint
            ? <><b style={{ color: '#9dff7d' }}>◉</b> {SHORT[waypoint] || waypoint} is waiting · tap any place to go</>
            : followMarks.length
              ? <>Tap to enter a venue · <b style={{ color: '#4fd1ff' }}>◉</b> mine · <b style={{ color: '#ffd27d' }}>◉</b> following — tap a face to travel</>
              : 'Tap a location to go inside'}
      </div>
    </div>
  );
  if (!big) return panel;
  // Into the page itself, not the HUD's layer: the HUD sits low (z 10) and the
  // joystick would otherwise draw over the dimmed street.
  return createPortal(
    <div
      onClick={() => setExpanded(false)}
      style={{
        position: 'fixed', inset: 0, zIndex: 80, pointerEvents: 'auto', display: 'grid', placeItems: 'center',
        background: 'rgba(6,3,0,0.55)', padding: 16, boxSizing: 'border-box',
      }}
    >
      {panel}
    </div>,
    document.body,
  );
}

// HUD — minimal chrome. Entry gate, navigation (up/back), broadcast/pulse
// toggle, now-playing ticker, and a status line so it's never ambiguous.
export default function Hud() {
  const zoom = useWorldStore((s) => s.zoom);
  const setZoom = useWorldStore((s) => s.setZoom);
  const interior = useWorldStore((s) => s.interior);
  const exitInterior = useWorldStore((s) => s.exitInterior);
  const musicOn = useWorldStore((s) => s.musicOn);
  const toggleMusic = useWorldStore((s) => s.toggleMusic);
  const nowPlaying = useWorldStore((s) => s.nowPlaying);
  const setModulation = useWorldStore((s) => s.setModulation);

  // ── LIKING WHAT IS ON ──────────────────────────────────────────────────────
  // Khari, 2026-08-06: "I often want to tag songs and cannot because there is no
  // interaction here." The ticker names the record and gave you nothing to do
  // about it, which made the world the one surface where hearing something you
  // like is a dead end.
  //
  // Held as a Set of track ids rather than a boolean, so the heart is still
  // filled if the station comes back round to the same record — and it is
  // SESSION state on purpose: reading the true liked-state would mean pulling
  // the user's whole likes list to answer a question about one song. The write
  // is idempotent (INSERT IGNORE), so the worst case of not knowing is a
  // no-op, and every other surface shows the real answer.
  const [liked, setLiked] = useState(() => new Set());
  const [likeBusy, setLikeBusy] = useState(false);

  const [status, setStatus] = useState('idle'); // idle | starting | live | pulse | offline
  // The entry gate now lives on the Loader (single screen). `entered` is lifted
  // to the store so the Loader's Enter button flips it (and starts audio + the
  // fly-in). Hud just reacts to it for the in-world chrome + audio status.
  const entered = useWorldStore((s) => s.entered);
  const setEntered = useWorldStore((s) => s.setEntered);

  // The town's local clock (Oakland) — re-evaluated with the world-time hook,
  // so a ?time override shows its pinned phase while the clock shows reality.
  const tod = useWorldTime(SLICE_DISTRICT.timezone);
  // With its zone ("2:42 PM PDT"): the city is on West Coast time, and a visitor
  // elsewhere has to know whose 2:42 it is (Khari, 2026-10-29). city/cityTime.
  const clockText = cityTime(serverNow(), { zone: true }); // shared world clock, not the device clock

  // Stop audio on unmount.
  useEffect(() => () => audioEngine.stop(), []);

  // Pull the modulation track for whatever is currently playing.
  useEffect(() => {
    const cur = nowPlaying?.current;
    if (nowPlaying?.playing && cur?.item_type === 'track' && cur?.track_id) {
      api.worldModulation(cur.track_id)
        .then((m) => setModulation(m?.modulation || m))
        .catch(() => setModulation(null));
    }
  }, [nowPlaying?.current?.track_id]);

  // ── THE RADIO COMES ON WITH THE FLY-IN ────────────────────────────────────
  //
  // Khari, 2026-08-07: "The radio should instead start with the fly-in."
  //
  // `entered` is the fly-in's own cue — the Loader raises it, and the character
  // picker HOLDS IT DOWN while it is open so the city stays still behind it
  // ("NOW the city may start: fly-in, station, the lot"). Hanging the station
  // off the same flag is what puts them on one clock; it used to start on the
  // Enter CLICK instead, which is a moment earlier and a whole screen away.
  //
  // MIRRORED, NOT LATCHED. The first cut of this latched on `entered`'s first
  // rise — and lost the race that made Khari hear the radio over the character
  // select anyway (2026-08-07: "that didnt change anything"): the Enter click
  // raises `entered`, but CharacterSelect only lowers it again AFTER its roster
  // fetch resolves. In that network-length window the latch fired, the station
  // started, and nothing ever told it the screen had reopened the pre-world
  // pause.
  //
  // So the station now FOLLOWS the flag both ways: up = play (the fly-in),
  // down = stop (a pre-world screen is up). The player's own mute stays theirs
  // because the manual toggle only exists in-world, where `entered` no longer
  // moves — this mirror can't fight it.
  const musicStarted = useRef(false);
  // …and not while the body picker is up (2026-11-01): it opens under the door
  // and is revealed by the same Enter that raises `entered`, so the station waits
  // for the choice, as the fly-in does (PlanetScene).
  const charSelect = useWorldStore((s) => s.charSelect);
  useEffect(() => {
    const live = entered && !charSelect;
    if (live && !musicStarted.current) {
      musicStarted.current = true;
      // Falls back to ambient exactly as before if the station will not play.
      audioEngine.setMusicOn(true).catch(() => audioEngine.setMusicOn(false).catch(() => {}));
    } else if (!live && musicStarted.current) {
      musicStarted.current = false;
      audioEngine.setMusicOn(false).catch(() => {});
    }
  }, [entered, charSelect]);

  // Reflect audio state in the status line.
  useEffect(() => {
    if (!entered) return;
    if (!musicOn) { setStatus('pulse'); return; }
    setStatus(nowPlaying?.playing ? 'live' : 'offline');
  }, [entered, musicOn, nowPlaying?.playing]);


  const onToggleMusic = async () => {
    /* ⏯ CONTROLS WHAT THE PLAYER SHOWS (Khari, 2026-10-30: at the plaza it showed
       the stage's song and toggled the radio, which then played over the set).
       While a city source is showing, it pauses / resumes THAT; the radio stays
       held by the stage or the street source as before. */
    if (stageNow || soundNow) { setCityMusicOff(!cityMusicOff); return; }
    const next = !musicOn;
    toggleMusic();
    // In the concert hall / cinema the "music" is the on-screen video's audio —
    // the PerformerVideo / TheaterScreen read musicOn to mute/unmute; don't drive
    // the district station here (it stays ducked under the venue).
    if (interior === 'concert-hall' || interior === 'theater') return;
    try { await audioEngine.setMusicOn(next, { mine: true }); } catch { /* stay silent */ }
  };

  const goUp = () => {
    if (zoom === ZOOM.INTERIOR) { exitInterior(); return; }
    if (zoom === ZOOM.STREET) { setZoom(ZOOM.DISTRICT); return; }
    if (zoom === ZOOM.DISTRICT) { setZoom(ZOOM.ORBIT); return; }
  };
  const goDown = () => {
    if (zoom === ZOOM.ORBIT) { setZoom(ZOOM.DISTRICT); return; }
    if (zoom === ZOOM.DISTRICT) { setZoom(ZOOM.STREET); return; }
  };

  const cur = nowPlaying?.current;
  // Only a real record can be liked — the DJ's own segments are not tracks and
  // carry no id to send.
  const likeableId = cur?.item_type === 'track' ? cur.track_id : null;
  const isLiked = !!likeableId && liked.has(likeableId);
  const toggleLike = async () => {
    if (!likeableId || likeBusy) return;
    setLikeBusy(true);
    const next = !isLiked;
    // Fill it immediately. A heart that waits on a round-trip before it responds
    // reads as a broken button, and this one gets pressed while walking.
    setLiked((s) => { const n = new Set(s); if (next) n.add(likeableId); else n.delete(likeableId); return n; });
    try {
      const { res } = next ? await api.likeTrack(likeableId) : await api.unlikeTrack(likeableId);
      if (!res.ok) throw new Error(`like ${res.status}`);
    } catch (err) {
      // Put it back. A heart that stays filled after a failed write is a lie the
      // user only finds out about somewhere else, looking for a song that never
      // made it into their likes.
      console.warn('[hud] like failed:', err.message);
      setLiked((s) => { const n = new Set(s); if (next) n.delete(likeableId); else n.add(likeableId); return n; });
    } finally { setLikeBusy(false); }
  };

  // A set on the plaza stage you can hear, or the DJ calling the next one
  // (components/city/CityStage), is what the bar says while it lasts.
  const stageNow = useCityStore((s) => s.stageNow);
  const cityMusicOff = useWorldStore((s) => s.cityMusicOff);
  const setCityMusicOff = useWorldStore((s) => s.setCityMusicOff);
  // A pause of the city's music lapses once no city source has been on for a
  // while (a set's 9 s between songs must not undo it).
  const soundNowForPause = useCityStore((s) => s.soundNow);
  useEffect(() => {
    if (!cityMusicOff || stageNow || soundNowForPause) return undefined;
    const t = setTimeout(() => setCityMusicOff(false), 30000);
    return () => clearTimeout(t);
  }, [cityMusicOff, stageNow, soundNowForPause, setCityMusicOff]);
  const djNow = useCityStore((s) => s.djNow);
  // Or the street's music you are standing by — a parked car, the record store
  // (components/city/CitySounds): whose it is, and what it is playing.
  const soundNow = useCityStore((s) => s.soundNow);
  const ticker = nowPlaying?.playing && cur
    ? (cur.item_type === 'track'
        ? `${cur.artist || cur.avatar_name || 'HypeTunes'} — ${cur.title || ''}`
        : `${SLICE_DISTRICT.name} DJ`)
    : `${SLICE_DISTRICT.name}`;

  const statusText = {
    idle: '', starting: 'Connecting to the broadcast…',
    live: 'Broadcast — live', pulse: 'Pulse — ambient soundscape',
    offline: 'Station quiet — ambient playing'
  }[status];

  const btn = {
    background: 'rgba(20,12,6,0.65)', color: '#ffd27d',
    border: '1px solid rgba(255,178,94,0.35)', borderRadius: 999,
    padding: '10px 16px', cursor: 'pointer', fontSize: 14, pointerEvents: 'auto'
  };

  const vw = useViewport();
  const narrow = vw < 640;
  // The top-right corner the minimap takes, so the right-hand column (docks,
  // Done: components/hudStack) stops short of it. The phone's chip is a row;
  // the desktop map is a panel ~190px tall.
  const mapUp = zoom === ZOOM.STREET || zoom === ZOOM.INTERIOR;
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--hud-corner-h', mapUp && !narrow ? '216px' : '60px');
    return () => root.style.removeProperty('--hud-corner-h');
  }, [mapUp, narrow]);
  const IS_TOUCH = typeof window !== 'undefined' && (('ontouchstart' in window) || navigator.maxTouchPoints > 0);
  const navBtn = { ...btn, padding: narrow ? '8px 12px' : '10px 16px', fontSize: narrow ? 12.5 : 14 };
  // The view toggle (above): with a body, first or third person.
  const hasBody = useHasBody();
  const wantThird = usePovStore((st) => st.wantThird);
  const togglePov = usePovStore((st) => st.toggle);
  const upLabel = zoom === ZOOM.INTERIOR ? (narrow ? 'Back' : 'Back to street')
    : zoom === ZOOM.STREET ? (narrow ? 'District' : 'Up to district')
    : (narrow ? 'Orbit' : 'Up to orbit');
  // Fullscreen toggle — works on Android/desktop; iOS Safari has no element
  // fullscreen (use Add to Home Screen for a standalone fullscreen app).
  const toggleFullscreen = () => {
    const el = document.documentElement;
    try {
      if (!document.fullscreenElement && !document.webkitFullscreenElement) (el.requestFullscreen || el.webkitRequestFullscreen)?.call(el);
      else (document.exitFullscreen || document.webkitExitFullscreen)?.call(document);
    } catch { /* unsupported */ }
  };
  const fsSupported = typeof document !== 'undefined' && (document.documentElement.requestFullscreen || document.documentElement.webkitRequestFullscreen);
  // iOS has no element Fullscreen API, so the real fullscreen path is Add to
  // Home Screen (launches the World standalone, no browser chrome). Show a prompt
  // in place of the (impossible) fullscreen button — unless already standalone.
  const isStandalone = typeof window !== 'undefined'
    && (window.navigator.standalone === true || window.matchMedia?.('(display-mode: standalone)').matches);
  const iosA2HS = !fsSupported && IS_TOUCH && !isStandalone;
  const [showA2HS, setShowA2HS] = useState(false);
  // Keep top/bottom chrome inside the notch/home-indicator safe areas.
  const safe = (side, base) => `max(${base}px, env(safe-area-inset-${side}))`;

  return (
    // Every piece of chrome below is positioned against THIS box, not the
    // viewport — so clamping it to a centred column (config.HUD_MAX) keeps the
    // whole HUD in one glance on an ultrawide without touching a single
    // left/right rule. No-op on 16:9 and narrower.
    <div style={{
      position: 'fixed', top: 0, bottom: 0, left: '50%', transform: 'translateX(-50%)',
      width: '100%', maxWidth: HUD_MAX, pointerEvents: 'none', zIndex: 10,
    }}>
      {/* Entry gate now lives on the Loader (single loading→enter screen). */}

      {/* In-world chrome */}
      {entered && (
        <>
          {/* Top-left: home (back to the website), navigation */}
          <div style={{ position: 'absolute', left: safe('left', 16), top: safe('top', 16), display: 'flex', gap: 8, alignItems: 'center' }}>
            <a
              href={HOME_URL} aria-label="HypeTunes home" title="Back to HypeTunes"
              style={{ ...navBtn, padding: 0, width: narrow ? 36 : 42, height: narrow ? 36 : 42, boxSizing: 'border-box', display: 'grid', placeItems: 'center', textDecoration: 'none' }}
            >
              <svg width={narrow ? 17 : 19} height={narrow ? 17 : 19} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M3 10.5 12 3l9 7.5" />
                <path d="M5.5 9v11h13V9" />
                <path d="M10 20v-6h4v6" />
              </svg>
            </a>
            {zoom !== ZOOM.ORBIT && (zoom !== ZOOM.STREET || HUD_UP_FROM_STREET) && (
              <button onClick={goUp} style={navBtn}>← {upLabel}</button>
            )}
            {(zoom === ZOOM.ORBIT || zoom === ZOOM.DISTRICT) && (
              <button onClick={goDown} style={navBtn}>
                {zoom === ZOOM.ORBIT ? (narrow ? 'District ↓' : 'Down to district ↓') : (narrow ? 'Street ↓' : 'Down to street ↓')}
              </button>
            )}
            {HUD_FULLSCREEN && fsSupported && (
              <button onClick={toggleFullscreen} title="Fullscreen" style={{ ...navBtn, padding: narrow ? '8px 11px' : '10px 13px' }}>⛶</button>
            )}
            {HUD_FULLSCREEN && iosA2HS && (
              <button onClick={() => setShowA2HS(true)} title="Fullscreen" style={{ ...navBtn, padding: narrow ? '8px 11px' : '10px 13px' }}>⛶</button>
            )}
            {/* FIRST / THIRD PERSON, ON SCREEN (2026-11-01, Khari: "Did we lose
                the POV toggle? I do not see it."). It had only ever been the V
                key — invisible, and impossible on a phone. Shown with a body,
                where the view can change: the street and walk-in rooms. */}
            {hasBody && (zoom === ZOOM.STREET || (zoom === ZOOM.INTERIOR && !!ROOMS[interior]?.firstPerson && !ROOMS[interior]?.lookOnly)) && (
              <button onClick={togglePov} title="Switch first / third person (V)" aria-pressed={wantThird}
                style={{ ...navBtn, padding: narrow ? '8px 11px' : '10px 13px', opacity: 0.85 }}>
                {narrow ? (wantThird ? '3rd' : '1st') : (wantThird ? '3rd person' : '1st person')}
              </button>
            )}
            {/* YOUR BODY (2026-11-01): reopens the picker to change mannequin
                (CharacterSelect, game/mannequins). The city waits under it. */}
            <button onClick={() => useWorldStore.getState().setCharSelect(true)} title="Change your body"
              style={{ ...navBtn, padding: narrow ? '8px 11px' : '10px 13px', opacity: 0.85 }}>
              {narrow ? '⚇' : 'Body'}
            </button>
            {!IS_GUEST && (
              <button onClick={logout} title="Log out" style={{ ...navBtn, padding: narrow ? '8px 11px' : '10px 13px', opacity: 0.85 }}>
                {narrow ? '⎋' : 'Log out'}
              </button>
            )}
          </div>

          {/* Top-center: the town clock — the district lives on its city's real time.
              Short on phones so it can't collide with the nav / map. */}
          {!(narrow && zoom === ZOOM.INTERIOR) && (
            <div style={{
              position: 'absolute', left: '50%', top: safe('top', 16), transform: 'translateX(-50%)',
              color: '#ffe9c4', background: 'rgba(20,12,6,0.55)',
              border: '1px solid rgba(255,178,94,0.3)', borderRadius: 999,
              padding: narrow ? '6px 11px' : '7px 16px', fontSize: narrow ? 11 : 12.5,
              letterSpacing: 1.2, textTransform: 'uppercase', whiteSpace: 'nowrap',
            }}>
              {narrow ? clockText : `${SLICE_DISTRICT.city} · ${clockText} · ${tod.phase}${weather.kind !== 'clear' && zoom === ZOOM.STREET ? ` · ${weather.kind}` : ''}`}
            </div>
          )}

          {/* Top-right: the clickable town map (street + interior) */}
          {(zoom === ZOOM.STREET || zoom === ZOOM.INTERIOR) && <TownMap narrow={narrow} />}

          {/* THE PLAYER (components/PlayerBar): what is on, ♥, ⏯ and the chat
              input, in one bar along the bottom — replacing the floating card,
              the Music button and the 💬 bubble. */}
          <PlayerBar
            compact={IS_TOUCH || narrow}
            ticker={stageNow ? `${stageNow.name} — ${stageNow.title}` : soundNow ? `${soundNow.artist ? `${soundNow.artist} — ` : ''}${soundNow.title}` : djNow ? djNow.text : ticker}
            statusText={stageNow ? (stageNow.where || 'Live · the plaza stage') : soundNow ? soundNow.where : djNow ? `${djNow.host} · on air` : statusText}
            musicOn={(stageNow || soundNow) ? !cityMusicOff : musicOn} onToggleMusic={onToggleMusic}
            art={stageNow ? (stageNow.art || null) : soundNow ? (soundNow.art || null) : (cur?.artwork_url || cur?.cover_image_url || cur?.image_url || null)}
            likeable={!IS_GUEST && !!likeableId} isLiked={isLiked} likeBusy={likeBusy} onLike={toggleLike}
          />

          {/* iOS "Add to Home Screen for fullscreen" prompt (no element
              Fullscreen API on iOS — this is the real path). */}
          {showA2HS && (
            <div
              onClick={() => setShowA2HS(false)}
              style={{
                position: 'absolute', inset: 0, zIndex: 30, pointerEvents: 'auto',
                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
                background: 'radial-gradient(ellipse at center, rgba(6,3,0,0.5) 30%, rgba(6,3,0,0.75) 100%)',
              }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  maxWidth: 330, width: '100%', background: 'rgba(20,12,6,0.96)',
                  border: '1px solid rgba(255,178,94,0.4)', borderRadius: 16, padding: '20px 20px 16px',
                  color: '#ffe9c4', fontFamily: 'system-ui, sans-serif', boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
                }}
              >
                <div style={{ fontSize: 17, fontWeight: 800, marginBottom: 6 }}>Play fullscreen</div>
                <div style={{ fontSize: 13.5, opacity: 0.85, lineHeight: 1.5, marginBottom: 14 }}>
                  iPhone &amp; iPad don&apos;t allow in-page fullscreen. Add Planet HypeTunes to your Home Screen and it opens fullscreen — no browser bars.
                </div>
                <ol style={{ margin: 0, paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {[
                    <>Tap the <b>Share</b> button
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" style={{ verticalAlign: '-2px', margin: '0 3px' }}>
                        <path d="M12 15V3m0 0L8 7m4-4l4 4" stroke="#ffd27d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M6 12H5a2 2 0 00-2 2v5a2 2 0 002 2h14a2 2 0 002-2v-5a2 2 0 00-2-2h-1" stroke="#ffd27d" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                      in the toolbar</>,
                    <>Choose <b>Add to Home Screen</b></>,
                    <>Open it from your Home Screen</>,
                  ].map((step, i) => (
                    <li key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13.5, lineHeight: 1.45 }}>
                      <span style={{
                        flexShrink: 0, width: 20, height: 20, borderRadius: 999, background: '#ffb25e', color: '#1c1206',
                        fontSize: 12, fontWeight: 800, display: 'grid', placeItems: 'center',
                      }}>{i + 1}</span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>
                <button
                  onClick={() => setShowA2HS(false)}
                  style={{
                    marginTop: 16, width: '100%', background: 'rgba(255,178,94,0.16)', color: '#ffd27d',
                    border: '1px solid rgba(255,178,94,0.4)', borderRadius: 10, padding: '10px 0',
                    cursor: 'pointer', font: '600 14px system-ui',
                  }}
                >
                  Got it
                </button>
              </div>
            </div>
          )}

          <PickedReadout />
        </>
      )}
    </div>
  );
}

// ?pick=1 debug readout — shows the last clicked mesh so we can target a sign.
function PickedReadout() {
  const picked = useWorldStore((s) => s.picked);
  if (!picked) return null;
  return (
    <div style={{
      position: 'absolute', left: 16, bottom: 84, pointerEvents: 'auto',
      background: 'rgba(6,10,20,0.85)', border: '1px solid #4fd1ff', borderRadius: 10,
      color: '#cdefff', padding: '10px 14px', font: '12px ui-monospace, monospace', maxWidth: 380,
    }}>
      <div style={{ opacity: 0.7, marginBottom: 4 }}>PICKED MESH</div>
      <div>mesh: <b>{picked.name}</b></div>
      <div>material: <b>{picked.material}</b></div>
      <div>size: {picked.size.join(' × ')} m</div>
    </div>
  );
}